import {
  LEARNED_AUTO_FILL_LIBRARY_KIND,
  LEARNED_AUTO_FILL_LIBRARY_VERSION,
  MAX_LEARNED_LIBRARY_ENTRIES,
  MAX_LEARNED_LIBRARY_FILE_BYTES,
  type LearnedAutoFillLibraryDocumentV1,
  type LearnedAutoFillLibraryParseResult,
  type LearnedAutoFillPatternV1,
} from './learnedAutoFillContract';
import { sanitizeLearnedAutoFillPattern } from './learnedAutoFillPatterns';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isTimestamp(value: unknown): value is string {
  return typeof value === 'string' &&
    value.length <= 64 &&
    value.trim().length > 0 &&
    value.includes('T') &&
    Number.isFinite(Date.parse(value));
}

function textByteLength(value: string): number {
  return new Blob([value]).size;
}

export function parseLearnedAutoFillLibraryDocument(
  raw: string
): LearnedAutoFillLibraryParseResult {
  if (textByteLength(raw) > MAX_LEARNED_LIBRARY_FILE_BYTES) {
    return { ok: false, error: 'The dictionary file exceeds the 1 MiB limit.' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'The selected file is not valid JSON.' };
  }

  if (!isRecord(parsed) || parsed.contract !== LEARNED_AUTO_FILL_LIBRARY_KIND) {
    return { ok: false, error: 'The selected file is not a PipeEX dictionary.' };
  }
  if (parsed.schemaVersion !== LEARNED_AUTO_FILL_LIBRARY_VERSION) {
    return { ok: false, error: 'This dictionary version is not supported.' };
  }
  if (!Array.isArray(parsed.entries) || parsed.entries.length > MAX_LEARNED_LIBRARY_ENTRIES) {
    return { ok: false, error: 'A dictionary can contain at most 5,000 entries.' };
  }
  if (!isTimestamp(parsed.exportedAt)) {
    return { ok: false, error: 'The dictionary export timestamp is invalid.' };
  }

  const entries = parsed.entries.map(sanitizeLearnedAutoFillPattern);
  if (entries.some((entry) => entry === null)) {
    return { ok: false, error: 'The dictionary contains an invalid entry.' };
  }

  return {
    ok: true,
    document: {
      contract: LEARNED_AUTO_FILL_LIBRARY_KIND,
      schemaVersion: LEARNED_AUTO_FILL_LIBRARY_VERSION,
      exportedAt: parsed.exportedAt,
      entries: entries as LearnedAutoFillPatternV1[],
    },
  };
}

export function serializeLearnedAutoFillLibrary(
  entries: readonly LearnedAutoFillPatternV1[],
  exportedAt = new Date().toISOString()
): string {
  const document: LearnedAutoFillLibraryDocumentV1 = {
    contract: LEARNED_AUTO_FILL_LIBRARY_KIND,
    schemaVersion: LEARNED_AUTO_FILL_LIBRARY_VERSION,
    exportedAt,
    entries: [...entries],
  };
  return JSON.stringify(document, null, 2);
}
